import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotFound from "./NotFound";
import { Dropdown } from "primereact/dropdown";
import { get, post } from "../utils/api";
import ReusableTable from "../components/Table/ReusableTable";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Button from "../components/Button/Button";
import {
  Save,
  Eye,
  UserPlus,
  UserCheck,
  Edit3,
  ExternalLink,
  UploadCloud,
  Check,
  ShieldCheck
} from "lucide-react";

const MODULE_GROUPS = {
  CORE: [
    "Dashboard",
    "User Management",
    "User Master",
    "Role Master",
    "UI Configuration",
  ],
  FINANCE: [
    "Fund Master",
    "Distributor Product Mapped",
    "Product Mapped",
    "Transaction",
    "Transaction Fee Mismatch",
    "Transaction Details Report",
    "AMC Listing",
  ],
  OPERATIONS: [
    "Distributor Master",
    "Product Master",
    "Invoice",
    "OPS Dashboard",
    "KYC Listing",
    "Distributor Dashboard",
    "Sales Dashboard",
    "KYC",
    "Sales Listing",
    "Transaction Management",
    "Sales Head Dashboard",
    "Distributor Listing",
  ],
};

const PermissionToggle = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    className={`w-6 h-6 rounded-full border flex items-center justify-center cursor-pointer transition-all duration-300 ${checked
      ? "border-[#7963FF] bg-white"
      : "border-gray-200 bg-white hover:border-indigo-200"
      }`}
  >
    {checked && <Check size={14} strokeWidth={3} className="text-[#7963FF]" />}
  </div>
);

const UIConfigurationPage = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const userId = JSON.parse(localStorage.getItem("user_details") || "{}").id;

  useEffect(() => {
    if (!userId) navigate("/not", { replace: true });
  }, [userId, navigate]);

  useEffect(() => {
    async function fetchRoles() {
      try {
        const { data } = await get("/uirolemap/fetchActiveRoles");
        if (data?.Data) {
          setRoleOptions(
            data.Data.map((r) => ({
              label: r.role_code,
              value: r.id,
            }))
          );
        }
      } catch {
        toast.error("Error fetching roles");
      } finally {
        setLoadingRoles(false);
      }
    }
    fetchRoles();
  }, []);

  useEffect(() => {
    if (!selectedOption) {
      setTableData([]);
      return;
    }
    async function fetchMapping() {
      try {
        const { data } = await post("/uirolemap/fetchUIRoleMappingDetails", {
          role_id: selectedOption,
        });
        if (data?.Data) {
          // Group the data first
          const groups = { CORE: [], FINANCE: [], OPERATIONS: [], OTHERS: [] };
          data.Data.forEach((item) => {
            let group = "OTHERS";
            for (const [groupName, menus] of Object.entries(MODULE_GROUPS)) {
              if (menus.includes(item.title)) {
                group = groupName;
                break;
              }
            }
            groups[group].push({
              ui_id: item.ui_id,
              ui_menu: item.title,
              view: item.is_view,
              maker: item.is_maker,
              checker: item.is_checker,
              edit: item.is_edit,
              export: item.is_export,
              upload: item.is_upload,
            });
          });

          // Flatten with section headers
          const flattened = [];
          Object.entries(groups).forEach(([groupName, items]) => {
            if (items.length > 0) {
              flattened.push({
                ui_id: `section-${groupName}`,
                isSection: true,
                sectionTitle: groupName,
              });
              flattened.push(...items);
            }
          });

          setTableData(flattened);
        }
      } catch {
        toast.error("Error fetching UI mapping");
      }
    }
    fetchMapping();
  }, [selectedOption]);

  const handleToggle = (uiId, key) => {
    setTableData((prev) =>
      prev.map((row) =>
        row.ui_id === uiId ? { ...row, [key]: !row[key] } : row
      )
    );
  };

  const columns = useMemo(() => {
    const iconHeader = (label, Icon) => (
      <div className="flex items-center justify-center gap-2">
        <Icon size={16} className="text-gray-400" />
        <span className="text-[10px] font-bold text-gray-500 uppercase">{label}</span>
      </div>
    );

    const toggleCol = (label, Icon, key) => ({
      Header: iconHeader(label, Icon),
      accessor: key,
      size: "100px",
      Cell: ({ value, row }) => (
        <div className="flex justify-center mt-1">
          <PermissionToggle
            checked={value}
            onChange={() => handleToggle(row.original.ui_id, key)}
          />
        </div>
      ),
    });

    return [
      {
        Header: "UI Menus",
        accessor: "ui_menu",
        size: "250px",
        Cell: ({ value }) => <span className="text-sm font-semibold text-gray-600 pl-4">{value}</span>
      },
      toggleCol("View", Eye, "view"),
      toggleCol("Maker", UserPlus, "maker"),
      toggleCol("Checker", UserCheck, "checker"),
      toggleCol("Edit", Edit3, "edit"),
      toggleCol("Export", ExternalLink, "export"),
      toggleCol("Upload", UploadCloud, "upload"),
    ];
  }, []);

  const handleSave = async () => {
    if (!selectedOption) return;
    setSaving(true);

    try {
      const createList = tableData
        .filter((row) => !row.isSection)
        .map((row) => ({
          ui_id: row.ui_id,
          is_view: row.view,
          is_maker: row.maker,
          is_checker: row.checker,
          is_edit: row.edit,
          is_export: row.export,
          is_upload: row.upload,
        }));

      const { data } = await post("/uirolemap/createUIRoleMappingDetails", {
        role_id: selectedOption,
        user_id: userId,
        createList,
      });

      if (data.Success) {
        toast.success("Mapping saved successfully!");
      } else {
        toast.error(data.Message || "Save failed");
      }
    } catch (error) {
      toast.error("Error saving mapping");
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    let totalGranted = 0;
    let modulesConfigured = 0;
    let totalModules = 0;
    tableData.forEach((row) => {
      if (!row.isSection) {
        totalModules++;
        const rowSum = (row.view ? 1 : 0) + (row.maker ? 1 : 0) + (row.checker ? 1 : 0) +
          (row.edit ? 1 : 0) + (row.export ? 1 : 0) + (row.upload ? 1 : 0);
        totalGranted += rowSum;
        if (rowSum > 0) modulesConfigured++;
      }
    });
    return { totalGranted, modulesConfigured, totalModules };
  }, [tableData]);

  if (!userId) return <NotFound />;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 min-h-screen bg-gray-50/20">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Bar */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-indigo-50 rounded-lg">
            <ShieldCheck className="text-indigo-600" size={22} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800">UI Menus</h1>
            <p className="text-xs text-gray-400">Configure role-based access control</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="w-64">
            <Dropdown
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.value)}
              options={roleOptions}
              placeholder={loadingRoles ? "Loading roles..." : "Select User Role"}
              className="w-full custom-dropdown rounded-lg border-gray-200"
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !selectedOption}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all shadow-sm font-semibold text-sm"
          >
            {saving ? "Saving..." : <><Save size={16} /> Save Configuration</>}
          </Button>
        </div>
      </div>

      {selectedOption ? (
        <div className="space-y-4">
          <ReusableTable columns={columns} data={tableData} />

          {/* Footer Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-8 py-4 flex justify-between items-center">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 font-medium">Total Permissions Granted:</span>
                <span className="text-sm font-bold text-gray-700">{stats.totalGranted}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-medium">Modules Configured:</span>
              <span className="text-sm font-bold text-gray-700">{stats.modulesConfigured} / {stats.totalModules}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-20 bg-white rounded-xl border border-dashed border-gray-200">
          <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4 text-indigo-400">
            <ShieldCheck size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No Role Selected</h3>
          <p className="text-gray-400 max-w-xs text-center text-xs">Select a user role from the dropdown above to manage its UI permissions.</p>
        </div>
      )}
    </div>
  );
};

export default UIConfigurationPage;
